import {
  Avatar,
  Badge,
  Card,
  Cell,
  Column,
  Field,
  FieldGrid,
  Row,
  Spinner,
  Table,
  TableBody,
  TableContainer,
  TableHeader,
} from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { DateFormat, DateUtility } from '@utility/date';
import { useProviderInvoice } from '../../hooks/use-invoice';
import {
  invoiceStatus,
  partyName,
  type InvoiceParty,
  type ProviderInvoiceDetail,
} from '../../lib/invoices';
import { InvoiceHistory } from './InvoiceHistory';

const usd = (amount?: number) => CommonUtility.currencyFormat(amount, 'USD');
const day = (date?: string) =>
  DateUtility.formatDate(date, DateFormat.barChartMonth);

function Party({ label, party }: { label: string; party?: InvoiceParty }) {
  const name = partyName(party);

  return (
    <Field label={label}>
      {name ? (
        <span className="sk:flex sk:items-center sk:gap-2">
          <Avatar src={party?.logo?.url} name={name} size="xs" />
          {name}
        </span>
      ) : (
        '—'
      )}
      {party?.address ? (
        <span className="sk:block sk:font-normal sk:text-fg-secondary">
          {CommonUtility.addressText(party.address)}
        </span>
      ) : null}
      {party?.email ? (
        <span className="sk:block sk:font-normal sk:text-fg-secondary">
          {party.email}
        </span>
      ) : null}
    </Field>
  );
}

/* A figure under a table: Subtotal, Total. */
function Sum({ label, amount }: { label: string; amount?: number }) {
  return (
    <div className="sk:flex sk:justify-end sk:gap-6 sk:border-t sk:border-rule sk:px-3 sk:py-2 sk:text-dense sk:tracking-dense">
      <span className="sk:text-fg-secondary">{label}</span>
      <span className="sk:w-28 sk:font-medium sk:text-fg sk:tabular-nums">
        {usd(amount)}
      </span>
    </div>
  );
}

const LINE_COLUMNS = [
  { id: 'category', label: 'Category' },
  { id: 'service', label: 'Service' },
  { id: 'unit', label: 'Unit' },
  { id: 'description', label: 'Description' },
  { id: 'price', label: 'Unit price' },
  { id: 'quantity', label: 'Quantity' },
  { id: 'amount', label: 'Amount' },
];

function LineItems({ invoice }: { invoice: ProviderInvoiceDetail }) {
  const hasAdjustments = !!invoice.adjustments?.length;

  return (
    <Card title="Line items" bodyClassName="sk:p-0">
      <TableContainer>
        <Table aria-label="Line items">
          <TableHeader>
            {LINE_COLUMNS.map(column => (
              <Column
                key={column.id}
                id={column.id}
                isRowHeader={column.id === 'service'}
              >
                {column.label}
              </Column>
            ))}
          </TableHeader>
          <TableBody
            renderEmptyState={() => (
              <div className="sk:px-3 sk:py-8 sk:text-center sk:text-fg-tertiary">
                No line items
              </div>
            )}
          >
            {(invoice.lineItems ?? []).map((item, index) => (
              <Row key={item._id ?? index} id={item._id ?? String(index)}>
                <Cell>{item.category}</Cell>
                <Cell>{item.serviceName}</Cell>
                <Cell>{item.unit}</Cell>
                <Cell>{item.description}</Cell>
                <Cell className="sk:tabular-nums">
                  {usd(item.pricePerUnit)}
                </Cell>
                <Cell className="sk:tabular-nums">{item.quantity}</Cell>
                <Cell className="sk:tabular-nums">{usd(item.amount)}</Cell>
              </Row>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Sum
        label={hasAdjustments ? 'Subtotal' : 'Total'}
        amount={invoice.subtotal}
      />
    </Card>
  );
}

function Adjustments({ invoice }: { invoice: ProviderInvoiceDetail }) {
  return (
    <Card title="Adjustments" bodyClassName="sk:p-0">
      <TableContainer>
        <Table aria-label="Adjustments">
          <TableHeader>
            <Column id="label" isRowHeader>
              Label
            </Column>
            <Column id="type" className="sk:w-48">
              Type
            </Column>
            <Column id="amount" className="sk:w-36">
              Amount
            </Column>
          </TableHeader>
          <TableBody>
            {(invoice.adjustments ?? []).map((adjustment, index) => (
              <Row
                key={adjustment._id ?? index}
                id={adjustment._id ?? String(index)}
              >
                <Cell>{adjustment.label}</Cell>
                <Cell>{CommonUtility.toTitleCase(adjustment.type ?? '')}</Cell>
                <Cell className="sk:tabular-nums">
                  {usd(adjustment.amount)}
                </Cell>
              </Row>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Sum label="Total" amount={invoice.totalAmount} />
    </Card>
  );
}

export type InvoiceDetailProps = {
  providerId: string;
  invoiceId: string;
};

/** A WMS provider's invoice to a customer: who, when, what for, and its history beside it. */
export function InvoiceDetail({ providerId, invoiceId }: InvoiceDetailProps) {
  const { data: invoice, loading } = useProviderInvoice(providerId, invoiceId);
  const status = invoiceStatus(invoice?.status);

  return (
    <div className="sk:grid sk:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] sk:items-start sk:gap-3">
      <div className="sk:flex sk:min-w-0 sk:flex-col sk:gap-3">
        <Card
          title={
            invoice?.invoiceNumber
              ? `Invoice ${invoice.invoiceNumber}`
              : 'Invoice'
          }
          action={
            loading ? (
              <Spinner size="sm" label="Loading invoice" />
            ) : status.label ? (
              <Badge tone={status.tone}>{status.label}</Badge>
            ) : null
          }
        >
          <FieldGrid columns={4}>
            <Party label="Bill to" party={invoice?.customerOrganizationId} />
            <Party label="From" party={invoice?.issuedByOrganizationId} />
            <Field label="Invoice date">
              {day(invoice?.invoiceDate) || '—'}
            </Field>
            <Field label="Due date">{day(invoice?.dueDate) || '—'}</Field>
          </FieldGrid>
          {invoice?.memo ? (
            <div className="sk:mt-3">
              <Field label="Memo">{invoice.memo}</Field>
            </div>
          ) : null}
        </Card>
        {invoice ? <LineItems invoice={invoice} /> : null}
        {invoice?.adjustments?.length ? (
          <Adjustments invoice={invoice} />
        ) : null}
      </div>
      <InvoiceHistory providerId={providerId} invoice={invoice} />
    </div>
  );
}
